"use client"

import IconShoppingBag from "@/app/(site)/_components/Atoms/Icons/IconShoppingBag"
import { useFetchRecommendedProducts } from "@/app/(site)/_utils/_hooks/useFetchRecommendedProducts"
import { getLangCode, getMarketCodeFromCentraLocale } from "@/app/(site)/_utils/misc"
import {
    default as useEcommerceStore,
    default as useGlobalStore
} from "@/app/(site)/contexts/ecommerce/store"
import { RecommendationType } from "@/lib/depict-client/types/RecommendationType"
import { t } from "@/translations"
import React, { useMemo, useState } from "react"
import Heading from "../../Atoms/Heading"
import Loader from "../../Atoms/Loader"
import QuickShopModal from "../../Modules/Cart/QuickShop/QuickShopModal"
import ButtonPill from "../../Molecules/ButtonPill"
import RegularProductCard from "../../Organisms/Cards/ProductCards/RegularProductCard"
import AnimationSliderWrapper from "../Carousel/AnimationSliderWrapper"
import DepictCarouselTrackingScript from "./DepictCarouselTrackingScript"

type DepictCarouselProps = {
    data?: any
    isPdp?: boolean
    isCartPage?: boolean
    params: { slug: string; locale: string }
    hasQuickShop?: boolean
    isAboveTheFold?: boolean
    carouselIndex?: number
}

const DepictCarousel: React.FC<DepictCarouselProps> = ({
    data,
    isPdp,
    isCartPage,
    params,
    hasQuickShop = false,
    isAboveTheFold = false,
    carouselIndex
}) => {
    const langCode = getLangCode(params.locale) || "" // params from contentMapper and langCode from page
    const toggleQuickShop = useEcommerceStore(state => state.toggleQuickShopModal)
    const isQuickShopModalOpen = useGlobalStore(state => state.isQuickShopModalOpen)
    const [productUri, setProductUri] = useState("")

    const depictType = data.depictRecommendationType || RecommendationType.FOR_YOU

    const productIds = useMemo(() => {
        const excludeProductIds =
            depictType === RecommendationType.LANDING_PAGE ||
            depictType === RecommendationType.RECENTLY_VIEWED ||
            depictType === RecommendationType.FOR_YOU

        if (excludeProductIds) {
            return undefined
        }

        return (
            data?.items
                ?.filter(
                    (item: { depictProductDisplayId?: string }) =>
                        item?.depictProductDisplayId
                )
                .map(
                    (item: { depictProductDisplayId: string }) =>
                        item.depictProductDisplayId
                ) || []
        )
    }, [JSON.stringify(data?.items?.map((item: any) => item.depictProductDisplayId))])

    const { groupedProductsRecord, loading } = useFetchRecommendedProducts({
        productIds,
        depictType
    })

    if (loading) return <Loader />

    if (
        !groupedProductsRecord ||
        Object.values(groupedProductsRecord).every(
            value => !Array.isArray(value) || value.length === 0
        )
    ) {
        return
    }

    const handleOpenQuickShop = (productUri: string) => {
        setProductUri(productUri)
        toggleQuickShop()
    }

    const handleClose = () => {
        setProductUri("")
        toggleQuickShop()
    }

    /**
     * This is done in the ContentMapper, but we need to do it here as well for Cart/Product-page
     */
    if (isCartPage || isPdp) {
        const marketCode = getMarketCodeFromCentraLocale(params?.locale || "")

        const isVisible = marketCode && data[marketCode] !== false
        if (!isVisible) {
            return null
        }
    }

    const carouselTitle =
        data?.title?.[langCode] ||
        data?.title?.["en"] ||
        data?.title ||
        t(langCode, "product_page.recommended_products")
    const carouselId = `depict-carousel-${carouselTitle}-${carouselIndex || 0}`
        .toLowerCase()
        .replace(/\s+/g, "-")

    // Build visible product groups and products (adjust .slice(0, 9) if needed)
    const visibleProductGroups = Object.values(groupedProductsRecord)
        .filter(Array.isArray)
        .slice(0, 9)
    // Only take the first product from each group for tracking (matching what's rendered)
    const visibleProducts = visibleProductGroups.map(group => group[0]).filter(Boolean)

    return (
        <>
            {isQuickShopModalOpen && hasQuickShop && (
                <QuickShopModal
                    productUri={productUri}
                    params={params || { slug: "", locale: "" }}
                    onClose={handleClose}
                />
            )}
            <div id={carouselId} className="flex flex-col gap-10 px-15 md:px-24">
                <Heading type="componentHeading" className="mb-10">
                    {carouselTitle}
                </Heading>
                <AnimationSliderWrapper>
                    {visibleProductGroups.map((productGroup, index) =>
                        Array.isArray(productGroup) ? (
                            <div
                                key={index}
                                className={
                                    "relative flex w-250 flex-shrink-0 flex-col justify-between md:w-390"
                                }
                            >
                                <RegularProductCard
                                    lazyLoad={!isAboveTheFold}
                                    productVariants={productGroup}
                                    listName="carousel"
                                    listId={`Homepage|${carouselTitle}`}
                                    listType="carousel"
                                    listHierarchy={`Homepage|${carouselTitle}`}
                                    index={index}
                                />
                                {hasQuickShop &&
                                    typeof handleOpenQuickShop === "function" && (
                                        <ButtonPill
                                            color="black"
                                            variant="primary"
                                            size="large"
                                            onClick={() => {
                                                handleOpenQuickShop(productGroup[0].uri)
                                            }}
                                            className="w-full md:w-fit"
                                            aria-label="Quick shop"
                                        >
                                            {t(langCode, "product_page.quick_shop")}
                                            <IconShoppingBag
                                                aria-hidden="true"
                                                className="ml-8 h-16 w-16"
                                            />
                                        </ButtonPill>
                                    )}
                            </div>
                        ) : null
                    )}
                </AnimationSliderWrapper>
            </div>

            {/* Tracking Scripts */}
            <DepictCarouselTrackingScript
                carouselId={carouselId}
                carouselTitle={carouselTitle}
                productItems={visibleProducts}
                locale={params.locale}
            />
        </>
    )
}

export default DepictCarousel
